import React, { useRef, useState } from "react";
import { api, resolveImg } from "../lib/api";
import { UploadCloud, X, Star, GripVertical, Camera, AlertCircle } from "lucide-react";
import { toast } from "sonner";

/**
 * Mobile-first photo uploader.
 * Props:
 *   photos: string[] — controlled list of URLs
 *   onChange: (next: string[]) => void
 *   max: number (default 12)
 */
export default function PhotoUploader({ photos = [], onChange, max = 12 }) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState({}); // id -> {progress, name, err}
  const [dragOver, setDragOver] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const remaining = max - photos.length;

  const uploadFiles = async (files) => {
    const list = Array.from(files).slice(0, remaining);
    if (list.length === 0) return;
    if (files.length > remaining) toast.info(`Only ${remaining} more allowed`);

    for (const f of list) {
      if (!["image/jpeg", "image/png", "image/webp"].includes(f.type)) {
        toast.error(`Unsupported: ${f.name}`, { description: "Use JPEG, PNG or WebP" });
        continue;
      }
      const id = `${Date.now()}-${Math.random()}`;
      setUploading(s => ({ ...s, [id]: { progress: 0, name: f.name } }));
      const fd = new FormData();
      fd.append("file", f);
      try {
        const r = await api.post("/uploads/image", fd, {
          onUploadProgress: (evt) => {
            const p = evt.total ? Math.round((evt.loaded / evt.total) * 100) : 0;
            setUploading(s => ({ ...s, [id]: { ...s[id], progress: p } }));
          },
        });
        onChange([...(photos || []), r.data.url]);
      } catch (e) {
        setUploading(s => ({ ...s, [id]: { ...s[id], err: e?.response?.data?.detail || "Upload failed" } }));
        toast.error("Upload failed", { description: e?.response?.data?.detail || f.name });
      } finally {
        setTimeout(() => setUploading(s => { const { [id]: _, ...rest } = s; return rest; }), 800);
      }
    }
  };

  const onPick = (e) => uploadFiles(e.target.files || []);
  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(e.dataTransfer?.files || []);
  };
  const removePhoto = (i) => onChange(photos.filter((_, idx) => idx !== i));
  const makePrimary = (i) => {
    if (i === 0) return;
    const next = [...photos];
    const [item] = next.splice(i, 1);
    next.unshift(item);
    onChange(next);
  };
  const move = (from, to) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...photos];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <div className="space-y-4">
      {/* Drop / Pick area */}
      <div
        data-testid="photo-uploader"
        onClick={() => fileInputRef.current?.click()}
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`relative border-2 border-dashed rounded-2xl p-6 md:p-10 text-center cursor-pointer transition-colors ${
          dragOver ? "border-amber-500 bg-amber-50 dark:bg-amber-950/20" : "border-slate-300 dark:border-slate-700 hover:border-amber-500"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          multiple
          capture="environment"
          className="hidden"
          onChange={onPick}
          data-testid="photo-file-input"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="h-12 w-12 rounded-full bg-amber-100 text-amber-600 grid place-items-center">
            <UploadCloud className="h-6 w-6" />
          </div>
          <div className="font-bold">Drop photos or tap to select</div>
          <div className="text-xs text-slate-500 max-w-sm">JPEG, PNG or WebP · up to 8 MB each · {remaining} slot{remaining === 1 ? "" : "s"} left</div>
          <div className="flex gap-2 mt-2 sm:hidden">
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-slate-900 text-white text-xs"><Camera className="h-3 w-3" /> Camera</span>
          </div>
        </div>
      </div>

      {/* Progress rows */}
      {Object.entries(uploading).length > 0 && (
        <div className="space-y-2">
          {Object.entries(uploading).map(([id, s]) => (
            <div key={id} className="flex items-center gap-3 text-xs">
              <div className="flex-1 truncate">{s.name}</div>
              {s.err ? (
                <span className="text-red-600 inline-flex items-center gap-1"><AlertCircle className="h-3 w-3" /> {s.err}</span>
              ) : (
                <>
                  <div className="w-40 h-1.5 bg-slate-200 dark:bg-slate-800 rounded overflow-hidden">
                    <div className="h-full bg-amber-500 transition-all" style={{ width: `${s.progress}%` }} />
                  </div>
                  <div className="w-8 text-right">{s.progress}%</div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Photo grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="photo-grid">
          {photos.map((p, i) => (
            <div
              key={p + i}
              draggable
              onDragStart={() => setDragIdx(i)}
              onDragOver={e => e.preventDefault()}
              onDrop={() => { if (dragIdx !== null) move(dragIdx, i); setDragIdx(null); }}
              className={`relative aspect-square rounded-xl overflow-hidden border-2 ${i === 0 ? "border-amber-500 ring-2 ring-amber-200" : "border-slate-200 dark:border-slate-700"} bg-slate-100 dark:bg-slate-800 group`}
            >
              <img src={resolveImg(p)} className="h-full w-full object-cover" alt="" />
              {i === 0 && (
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold uppercase">
                  <Star className="h-3 w-3" /> Cover
                </span>
              )}
              <div className="absolute inset-x-0 bottom-0 p-2 flex gap-1 opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/70 to-transparent">
                {i !== 0 && (
                  <button type="button" onClick={() => makePrimary(i)}
                    className="text-[10px] px-2 py-1 rounded bg-white/90 text-slate-900 font-semibold flex-1" data-testid={`make-primary-${i}`}>
                    Make cover
                  </button>
                )}
                <button type="button" onClick={() => removePhoto(i)} aria-label="Remove"
                  className="h-6 w-6 rounded-full bg-red-600 text-white grid place-items-center" data-testid={`remove-photo-${i}`}>
                  <X className="h-3 w-3" />
                </button>
              </div>
              <div className="absolute top-2 right-2 h-6 w-6 rounded-full bg-slate-900/70 text-white grid place-items-center opacity-60 group-hover:opacity-100">
                <GripVertical className="h-3 w-3" />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
